import pluralize from 'pluralize'
import { useEffect, useState } from 'react'

import { FvButton, LoadMore } from '@fv/client-components'
import type { NotificationFilter } from '@fv/models'
import { toggleArrayItem } from '@fv/models/core'

import { FooterBar } from '@/components/FooterBar'
import InfoBox from '@/components/InfoBox'
import YesNoToggle from '@/components/inputs/YesNoToggle'
import { Loading } from '@/components/Loading'
import { ShipperTooltip } from '@/components/ShipperTooltip'

import {
  useArchiveMultipleNotifications,
  useMarkMultipleNotificationsAsRead,
} from './mutations'
import { NotificationItem } from './NotificationItem'
import { type Notification, useNotifications } from './queries'

type NotificationListingProps = {
  sideBarFilter: NotificationFilter
  activeNotificationId: string | null
  onSelect: (notification: Notification | undefined) => void
  onArchive: () => void
}

export const NotificationListing = ({
  sideBarFilter,
  activeNotificationId,
  onSelect,
  onArchive,
}: NotificationListingProps) => {
  const [filter, setFilter] = useState<NotificationFilter>({})
  const archiveMultiple = useArchiveMultipleNotifications()
  const markMultipleAsRead = useMarkMultipleNotificationsAsRead()
  const notificationsQuery = useNotifications({
    ...sideBarFilter,
    ...filter,
  })
  const [checked, setChecked] = useState<string[]>([])

  const notifications = notificationsQuery.data ?? []
  const unreadSelected = checked.length
    ? notificationsQuery.data?.filter(
        n => !n.readDate && checked.includes(n._id),
      )
    : []

  // biome-ignore lint/correctness/useExhaustiveDependencies: unchecked on filter change
  useEffect(() => {
    setChecked([])
    onSelect(undefined)
  }, [sideBarFilter, filter])

  const handleArchiveMultiple = async () => {
    await archiveMultiple.mutateAsync(checked)
    setChecked([])
    onArchive()
  }

  const handleMarkMultipleAsRead = async () => {
    await markMultipleAsRead.mutateAsync(unreadSelected.map(n => n._id))
    setChecked([])
  }

  return (
    <>
      <div className="overflow-auto bg-fv-gray-100 px-6 py-8">
        <nav className="flex items-center mb-6 gap-x-4">
          <ShipperTooltip label="Select all toggle">
            <FvButton
              icon="check-double"
              iconClass="ml-1"
              onClick={() =>
                setChecked(
                  checked.length === notifications.length
                    ? []
                    : notifications.map(n => n._id),
                )
              }
            />
          </ShipperTooltip>
          <div className="flex items-center">
            <YesNoToggle
              onOff
              checked={filter.onlyUnread ?? false}
              label="Show unread only"
              name="showUnreadOnly"
              onChange={e =>
                setFilter({ ...filter, onlyUnread: e.target.checked })
              }
            />
          </div>
          <div className="flex items-center">
            <YesNoToggle
              onOff
              checked={filter.includeArchived ?? false}
              label="Include archived"
              name="includeArchived"
              onChange={e =>
                setFilter({ ...filter, includeArchived: e.target.checked })
              }
            />
          </div>
        </nav>
        <div className="flex flex-col gap-y-3">
          {notificationsQuery.isLoading && <Loading />}
          {!notificationsQuery.isLoading && notifications.length === 0 ? (
            <InfoBox>No notifications found</InfoBox>
          ) : (
            <div className="flex flex-col gap-y-2">
              {notifications.map(n => (
                <NotificationItem
                  key={n._id}
                  notification={n}
                  onSelect={() => onSelect(n)}
                  isActive={n._id === activeNotificationId}
                  checked={checked.includes(n._id)}
                  onCheckboxChange={isChecked =>
                    setChecked(toggleArrayItem(checked, n._id, isChecked))
                  }
                />
              ))}
              <LoadMore
                fetchNextPage={notificationsQuery.fetchNextPage}
                hasNextPage={notificationsQuery.hasNextPage}
                isLoading={notificationsQuery.isFetchingNextPage}
              />
            </div>
          )}
        </div>
      </div>

      {checked.length > 0 && (
        <FooterBar jc className="row-[-1] col-span-full">
          <FvButton
            theme="default"
            icon="archive"
            loading={archiveMultiple.isPending}
            onClick={handleArchiveMultiple}
          >
            <span>
              Archive {checked.length}{' '}
              {pluralize('notification', checked.length)}
            </span>
          </FvButton>
          {unreadSelected.length > 0 && (
            <FvButton
              theme="default"
              icon="check"
              onClick={handleMarkMultipleAsRead}
            >
              <span>
                Mark {unreadSelected.length}{' '}
                {pluralize('notification', unreadSelected.length)} as read
              </span>
            </FvButton>
          )}
        </FooterBar>
      )}
    </>
  )
}
